import { User } from 'types'

const doistEmailRegex = /@doist\.(com|io|test)$/i

/**
 * Gets user details from localStorage variable managed by twist-web.
 *
 * It will fall back to getUserFromServer in case localStorage is empty
 * and auth cookie is present (rare case).
 *
 * This is a client-side only function.
 *
 */
export async function getUser(): Promise<User | undefined> {
    const rawSession = localStorage.getItem('session_user')
    if (rawSession) {
        return new Promise((resolve) => {
            const json = JSON.parse(rawSession) as User
            json.isDoister = json.email !== undefined && doistEmailRegex.test(json.email)
            resolve(json)
        })
    } else if (document.cookie.includes(process.env.AUTH_COOKIE_KEY)) {
        return getUserFromServer()
    }
}

/**
 * Gets user details from authenticated API endpoint.
 *
 * On server-side it expects auth cookie value to be explicitly forwarded.
 * On client-side it will forward all cookies by default.
 */
export async function getUserFromServer(authCookieValue?: string): Promise<User | undefined> {
    const url = `${process.env.API_BASE_URL}/api/v3/users/get_session_user`
    const headers = new Headers({
        'Content-Type': 'application/json',
    })

    if (authCookieValue) {
        headers.set('cookie', `${process.env.AUTH_COOKIE_KEY}=${authCookieValue}`)
    }

    const abortController = new AbortController()
    const timeoutId = setTimeout(() => abortController.abort(), 10000)
    const res = await fetch(url, {
        method: 'GET',
        credentials: 'include',
        headers,
        signal: abortController.signal,
    })
    clearTimeout(timeoutId)

    if (res.status === 200) {
        return res.json()
    }
}
